'use client'

import type { KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import * as Dialog from '@radix-ui/react-dialog'

import { SearchFooter } from './components/SearchFooter'
import { SearchHeader } from './components/SearchHeader'
import { SearchResults } from './components/SearchResults'
import { DEFAULT_SEARCH_PLACEHOLDER } from './const/defaultSearchPlaceholder.const'
import type { SearchModalItem } from './interfaces/modalItem.interface'
import type { SearchModalProps } from './SearchModal.types'
import { flattenSearchSections } from './utils/flattenSearchSections'

import styles from './SearchModal.module.scss'

export function SearchModal({
  open,
  onOpenChange,
  query,
  onQueryChange,
  tabs,
  activeTabId,
  onTabChange,
  filterChips,
  activeFilterChipId,
  onFilterChipChange,
  sections,
  isLoading = false,
  emptyState,
  placeholder = DEFAULT_SEARCH_PLACEHOLDER,
  onItemSelect,
}: SearchModalProps) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [selectedIndex, setSelectedIndex] = useState(0)

  useEffect(() => {
    if (open) {
      const id = window.setTimeout(() => inputRef.current?.focus(), 0)
      return () => window.clearTimeout(id)
    }

    return undefined
  }, [open])

  const flatRows = useMemo(() => flattenSearchSections(sections), [sections])
  const flatCount = flatRows.length

  useEffect(() => {
    setSelectedIndex(0)
  }, [query])

  useEffect(() => {
    if (flatCount === 0) {
      setSelectedIndex(0)
      return
    }

    const max = flatCount - 1
    setSelectedIndex((prev) => Math.min(prev, max))
  }, [flatCount, sections])

  const handleActivateItem = useCallback(
    (item: SearchModalItem) => {
      onItemSelect?.(item)
    },
    [onItemSelect]
  )

  const handleKeyDownRoot = useCallback(
    (event: ReactKeyboardEvent<HTMLDivElement>) => {
      if (flatCount === 0) {
        return
      }

      switch (event.key) {
        case 'ArrowDown': {
          event.preventDefault()

          const next = flatCount > 1 ? (selectedIndex + 1) % flatCount : selectedIndex

          setSelectedIndex(next)

          break
        }
        case 'ArrowUp': {
          event.preventDefault()

          const next = flatCount > 1 ? (selectedIndex - 1 + flatCount) % flatCount : selectedIndex

          setSelectedIndex(next)

          break
        }

        case 'Enter': {
          event.preventDefault()
          const picked = flatRows[selectedIndex]?.item

          if (picked !== undefined) {
            handleActivateItem(picked)
          }

          break
        }

        case 'Escape': {
          event.preventDefault()
          onOpenChange(false)
          break
        }

        default:
          break
      }
    },
    [flatRows, flatCount, handleActivateItem, onOpenChange, selectedIndex]
  )

  const showEmptyPlaceholder = flatRows.length === 0 && !isLoading && emptyState !== undefined

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <div className={styles.portalRoot}>
          <Dialog.Overlay className={styles.overlay} />
          <Dialog.Content
            aria-describedby={undefined}
            className={styles.panel}
            onKeyDownCapture={(event) => {
              handleKeyDownRoot(event)
            }}
          >
            <Dialog.Title className={styles.visuallyHidden}>Application search</Dialog.Title>

            <div className={styles.mobileHandleWrap} aria-hidden>
              <div className={styles.mobileHandle} />
            </div>

            <SearchHeader
              activeTabId={activeTabId}
              inputRef={inputRef}
              isLoading={isLoading}
              placeholder={placeholder}
              query={query}
              tabs={tabs}
              onQueryChange={onQueryChange}
              onTabChange={onTabChange}
            />

            <SearchResults
              activeFilterChipId={activeFilterChipId}
              emptyState={emptyState}
              filterChips={filterChips}
              isLoading={isLoading}
              query={query}
              sections={sections}
              selectedIndex={selectedIndex}
              showEmptyPlaceholder={showEmptyPlaceholder}
              onFilterChipChange={onFilterChipChange}
              onItemHover={setSelectedIndex}
              onItemSelect={handleActivateItem}
            />

            <SearchFooter />
          </Dialog.Content>
        </div>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
